// Session/SpecialSessionFormFiller.tsx
import { useEffect, useMemo, useState } from 'react';
import { Center, Loader, Alert, Stack } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconForms } from '@tabler/icons-react';

import {
  useSessionCustomFieldService,
  useCustomFieldTypeService,
  useRegistrationService,
  CustomFieldType,
  SessionCustomFieldModelView,
  RegistrationCustomSessionDataModelView,
} from '@actschurch/ccms-sdk';
import { DynamicForm } from '@/components/advanced/FormBuilder';
import { toBuilderField } from '@/components/advanced/FormBuilder/adapters/createCustomFieldAdapter'; 
import type { FieldOption } from '@/components/advanced/FormBuilder/types/BuilderField';
import { EmptyState } from '../Components/EmptyState';

export interface SpecialSessionFormFillerProps {
  courseSessionID: number;
  registrationSessionID: number;
  memberID?: number | null;
  /** Choices for 'member' questions — the registration's members. */
  memberOptions?: FieldOption[];
  onSubmitted?: () => void;
  // Triggered when we want the parent to know if there is existing data or not
  onDataStateChange?: (hasData: boolean) => void; 
}

export function SpecialSessionFormFiller({
  courseSessionID,
  registrationSessionID,
  memberID = null,
  memberOptions,
  onSubmitted,
  onDataStateChange,
}: SpecialSessionFormFillerProps) {
  const sessionCustomField = useSessionCustomFieldService();
  const customFieldTypeService = useCustomFieldTypeService();
  const registrationService = useRegistrationService();

  const [fieldTypes, setFieldTypes] = useState<CustomFieldType[] | null>(null);
  const [definitions, setDefinitions] = useState<SessionCustomFieldModelView[] | null>(null);
  const [existingData, setExistingData] = useState<RegistrationCustomSessionDataModelView[]>([]);
  const [values, setValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Tracks if the user has modified anything from the initial DB state
  const [isDirty, setIsDirty] = useState(false);

  const loadData = () => {
    Promise.all([
      customFieldTypeService.list(),
      sessionCustomField.byCourseSession(courseSessionID),
      registrationService.getCustomSessionData(registrationSessionID), 
    ]).then(([types, defs, data]) => {
      setFieldTypes(types);
      setDefinitions(defs);
      setExistingData(data);
      onDataStateChange?.(data.length > 0);
    }).catch((e) => setError(e?.message ?? 'Failed to load form.'));
  };

  useEffect(() => {
    loadData();
  }, [customFieldTypeService, sessionCustomField, registrationService, courseSessionID, registrationSessionID]);

  const typesById = useMemo(
    () => new Map((fieldTypes ?? []).map((t) => [t.typeID, t])),
    [fieldTypes]
  );

  const builderFields = useMemo(() => {
    if (!definitions) return [];
    return definitions
      .filter((d) => d.isActive)
      .sort((a, b) => a.orderNo - b.orderNo)
      .map((d) => toBuilderField(d, typesById));
  }, [definitions, typesById]);

  // Pre-fill values from existing captured data, keyed by fieldKey.
  useEffect(() => {
    if (!definitions || existingData.length === 0) {
      setValues({});
      setIsDirty(false);
      return;
    }
    const byFieldID = new Map(definitions.map((d) => [d.sessionCustomFieldID, d]));
    const initial: Record<string, any> = {};
    for (const row of existingData) {
      const def = byFieldID.get(row.sessionCustomFieldID);
      if (!def || !def.fieldKey) continue;
      initial[def.fieldKey] =
        row.valueBoolean ?? row.valueNumber ?? row.valueDecimal ?? row.valueDateTime ?? row.valueText ?? row.value ?? '';
    }
    setValues(initial);
    setIsDirty(false); // reset dirty tracking on initial load
  }, [definitions, existingData]);

  const existingByFieldID = useMemo(
    () => new Map(existingData.map((d) => [d.sessionCustomFieldID, d])),
    [existingData]
  );

  const handleSubmit = async (formValues: Record<string, any>) => {
    if (!definitions) return;
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        definitions
          .filter((d) => d.isActive)
          .map(async (def) => {
            const raw = formValues[def.fieldKey ?? ''];
            if (raw === undefined || raw === null || raw === '') return;
            const existing = existingByFieldID.get(def.sessionCustomFieldID);
            const value = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);

            if (existing) {
              await registrationService.updateCustomSessionData(existing.registrationCustomSessionDataID, { 
                value,
                memberID,
              });
            } else {
              await registrationService.addCustomSessionData(registrationSessionID, { 
                sessionCustomFieldID: def.sessionCustomFieldID,
                memberID,
                value,
              });
            }
          })
      );
      setSubmitted(true);
      setIsDirty(false);
      loadData(); // refresh
      onSubmitted?.();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save answers.');
    } finally {
      setSaving(false);
    }
  };
  
  if (!fieldTypes || !definitions) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  // Determine if we should show the save/submit button:
  const hasExistingData = existingData.length > 0;
  const showSubmitButton = !hasExistingData || isDirty;

  if (builderFields.length === 0) {
  return (
    <EmptyState
      icon={IconForms}
      title="No questions set up"
      description="This session doesn't have a custom form yet. Add fields to it to start collecting answers."
      compact
    />
  );
}

  return (
    <Stack gap="md" className="custom-form-filler-container">
      {submitted && (
        <Alert
          color="green"
          icon={<IconCheck size={16} />}
          withCloseButton
          onClose={() => setSubmitted(false)}
        >
          Answers saved successfully.
        </Alert>
      )}
      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <DynamicForm
        fields={builderFields}
        values={values}
        memberOptions={memberOptions}
        onChange={(key, v) => {
          setValues((prev) => ({ ...prev, [key]: v }));
          setIsDirty(true); // User changed something -> make button reappear
          if (submitted) setSubmitted(false);
        }}
        onSubmit={handleSubmit}
        submitLabel={saving ? 'Saving…' : 'Save Answers'}
        {...(showSubmitButton ? {} : { submitLabel: '' })} 
      />
      
      {/* 
        This style injector targets ANY button element inside our custom-form-filler-container 
        except for delete/clear actions (which are on the other tab anyway).
      */}
      {!showSubmitButton && (
        <style dangerouslySetInnerHTML={{ __html: `
          .custom-form-filler-container button,
          .custom-form-filler-container .mantine-Button-root {
            display: none !important;
          }
        `}} />
      )}
    </Stack>
  );
}

export default SpecialSessionFormFiller;